import { Box, Spinner, ButtonGroup, Spacer } from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { sum } from 'lodash';
import { AutodeskViewing } from '../components/viewers/common/AutodeskViewing';
import {
  ForgeViewerProvider,
  useViewer,
  DbIdsPerModel,
} from '../components/common/ForgeViewer';
import {
  GetProjectSettingsDocument,
  GetShapesForProjectDeepDocument,
  GetOrderDeepDocument,
  GetOrderDeepQuery,
  OrderEntryDeepFragment,
  SheetShapeDeepFragment,
  ShapeDeepFragment,
} from '../gql/graphql';
import { extractFirstHexColor } from '../utils/color-util';
import {
  ViewerMode,
  ViewerModeProvider,
} from '../components/common/viewer-mode';
import GhostModeButton from '../components/common/GhostModeButton';
import IsolatedElementsButton from '../components/common/forgeViewerInteractions/IsolatedElementsButton';
import HiddenElementsButton from '../components/common/forgeViewerInteractions/HiddenElementsButton';
import MeasureToolButton from '../components/common/forgeViewerInteractions/MeasureToolButton';
import {
  SheetViewerContext,
  SheetViewerContextType,
} from '../components/common/SheetViewer';
import {
  SheetsViewerV2Context,
  SheetsViewerV2ContextType,
} from '../components/viewers/SheetsViewerV2/SheetsViewerV2.context';
import { ActiveTool } from '../components/viewers/SheetsViewerV2/enums/ActiveTool';
import { useLocalStorage } from '../hooks/local-storage';
import { ForgeContextMenuProvider } from '../services/viewer/ForgeContextMenu';
import { SheetContextMenuProvider } from '../services/viewer/SheetsContextMenu';
import { getShapeUrn } from '../services/viewer/ShapesManager';
import { useVisibility } from '../services/viewer/visibility';
import { useColorization } from '../services/viewer/colorization';
import { SelectedEntryContext } from './SelectedEntryContext';
import { CachedEntry } from './MobileOrderEntries';
import { Point2 } from 'src/domain/geometry/geometric-types';
import { ProjectHistoryProvider } from 'src/components/project/project-history/hooks/useProjectHistory';

interface Params {
  projectId: string;
  orderId?: string;
  '*': string | undefined;
}

// New component for rendering project shapes
interface ProjectShapesRendererProps {
  projectId: string;
  shapes: ShapeDeepFragment[];
}

function ProjectShapesRenderer({
  projectId,
  shapes,
}: ProjectShapesRendererProps): null {
  const { shapesManager } = useViewer();
  const shapeUrn = getShapeUrn(projectId);
  const activeRenderedUrnRef = useRef<string | null>(null);

  // Effect for rendering shapes
  useEffect(() => {
    if (!shapesManager || !shapeUrn || shapes.length === 0) {
      if (!shapeUrn && activeRenderedUrnRef.current) {
        activeRenderedUrnRef.current = null;
      }
      return;
    }

    if (activeRenderedUrnRef.current !== shapeUrn) {
      shapesManager.renderShapes(shapeUrn, shapes);
      activeRenderedUrnRef.current = shapeUrn;
    } else {
      shapesManager.renderShapes(shapeUrn, shapes);
    }
  }, [shapesManager, shapeUrn, shapes]);

  // Effect for cleaning up shapes when shapeUrn changes or component unmounts
  useEffect(() => {
    const managerAtSetup = shapesManager;
    const urnTrackedByThisEffect = shapeUrn;

    if (!urnTrackedByThisEffect || !managerAtSetup) {
      return;
    }

    return () => {
      managerAtSetup.unrenderShapes(urnTrackedByThisEffect);
      if (activeRenderedUrnRef.current === urnTrackedByThisEffect) {
        activeRenderedUrnRef.current = null;
      }
    };
  }, [shapeUrn]); // This effect intentionally ONLY depends on `shapeUrn`.

  return null; // This component does not render layout
}

function MobileViewerSettings(): React.ReactElement {
  const {
    viewer,
    visibilityManager: {
      reset,
      hiddenElements,
      isolatedElements,
      setHiddenElements,
      setIsolatedElements,
    },
  } = useViewer();

  const [isMeasureActive, setMeasureActive] = useState(false);

  const measureExtension = useMemo(() => {
    const extension = viewer?.getExtension(
      'Autodesk.Measure'
    ) as Autodesk.Extensions.Measure.MeasureExtension | null;
    extension?.setUnits('m');
    extension?.setPrecision(3);
    return extension;
  }, [viewer]);

  const numHiddenElements = sum(
    Object.values(hiddenElements).map((v) => v.length)
  );
  const numIsolatedElements = sum(
    Object.values(isolatedElements).map((v) => v.length)
  );

  return (
    <Box position="absolute" top={4} left={4} right={4} zIndex={10}>
      <ButtonGroup gap={1} width="100%">
        <GhostModeButton />
        <MeasureToolButton
          isMeasureActive={isMeasureActive}
          onToggleMeasure={() => {
            isMeasureActive
              ? measureExtension?.deactivate()
              : measureExtension?.activate('distance');
            setMeasureActive((active) => !active);
          }}
        />
        <Spacer />
        <IsolatedElementsButton
          numIsolatedElements={numIsolatedElements}
          onClearIsolated={() => setIsolatedElements({})}
        />
        <HiddenElementsButton
          numHiddenElements={numHiddenElements}
          onClearHidden={() => setHiddenElements({})}
        />
      </ButtonGroup>
    </Box>
  );
}

// eslint-disable-next-line complexity
export default function ForgeViewerMobile(): React.ReactElement {
  const { projectId, orderId, '*': rest } = useParams<keyof Params>() as Params;
  // This ref is for the GridItem that will host the viewer.
  // ForgeViewerProvider will internally find this element by its ID.
  const viewerHostRef = useRef<HTMLDivElement>(null);

  const [isViewingInitialized, setIsViewingInitialized] =
    useState<boolean>(false);
  const viewerModeContextValue = useMemo(
    () => ({
      viewerMode: ViewerMode.Models,
      setViewerMode: () => {
        /* No-op in mobile view */
      },
    }),
    []
  );

  const sheetViewerContextRef = useRef<HTMLDivElement>(null); // Ref for sheet viewer, if needed by its context mock
  const [activeSheetId, setActiveSheetId] = useState<string | null>(null);
  const [isShowingLabels, setIsShowingLabels] = useLocalStorage(
    'mobile-shape-labels',
    false
  );

  const sheetViewerValue = useMemo(
    (): SheetViewerContextType => ({
      viewerRef: sheetViewerContextRef, // Use the dedicated ref for sheet context
      pageNumber: 1,
      setPageNumber: (_pageNumber: number) => undefined,
      numberOfPages: null,
      activeSheetId,
      changeActiveSheet: (_sheetId: string) => undefined,
      page: null,
      addViewportChangeHandler: (_handler: any) => undefined,
      removeViewportChangeHandler: (_handler: any) => undefined,
      calibration: {
        calibration: null,
        editMode: {
          isActive: false,
          setActive: (_active: boolean) => undefined,
          calibration: [null, null] as [Point2 | null, Point2 | null],
          setCalibration: (_calibration: any) => undefined,
        },
      },
      colorization: {
        colorSection: (_colorFunc: any, _dbIdsPerModel: any) => undefined,
        clearColors: () => undefined,
        colorization: null,
      },
      shapesManager: {
        renderSheetShapes: (_urn: string, _shapes: SheetShapeDeepFragment[]) =>
          undefined,
        unrenderSheetShapes: (_urn: string) => undefined,
        renderedSheetShapes: {},
      },
      selectedDbIds: {} as DbIdsPerModel,
      toggleSelectDbId: () => undefined,
      setSelectedDbIds: () => undefined,
      selectDbIds: () => undefined,
      visibilityManager: {
        isolatedElements: {},
        setIsolatedElements: () => undefined,
        addToIsolation: () => undefined,
        hiddenElements: {},
        setHiddenElements: () => undefined,
        hide: () => undefined,
        show: () => undefined,
        hiddenSheetShapes: {},
        hideSheetShape: () => undefined,
        showSheetShape: () => undefined,
      },
      ghosting: {
        ghostMode: 0,
        setGhostMode: () => undefined,
      },
      showLabels: {
        isShowingLabels,
        setIsShowingLabels,
      },
      protanVisuals: {
        isShowingWindLoadZones: false,
        setIsShowingWindLoadZones: () => undefined,
        isShowingTaperedInsulationSlices: false,
        setIsShowingTaperedInsulationSlices: () => undefined,
        isShowingFasteners: false,
        setIsShowingFasteners: () => undefined,
      },
      isDrawing: false,
      setIsDrawing: () => undefined,
      isMeasuring: false,
      setIsMeasuring: () => undefined,
      propertyData: undefined,
    }),
    [activeSheetId, isShowingLabels, setIsShowingLabels]
  );

  const sheetsViewerV2Value = useMemo(
    (): SheetsViewerV2ContextType => ({
      projectId,
      viewer: {
        elementRef: sheetViewerContextRef,
        aggregated: null,
      },
      viewerMode: ViewerMode.Models,
      activeSheet: null,
      activeSheetId: null,
      changeActiveSheet: () => undefined,
      sheets: [],
      pagination: {
        pageNumber: 1,
        numberOfPages: 1,
        setPageNumber: () => undefined,
      },
      setPageNumber: () => undefined,
      calibration: {
        existing: null,
        tool: {
          isCalibrating: false,
          isComplete: false,
          isUpdating: false,
          enable: () => undefined,
          disable: () => undefined,
        },
        createOrUpdate: async () => undefined,
      },
      shapesManager: {
        renderSheetShapes: () => undefined,
        unrenderSheetShapes: () => undefined,
        renderedSheetShapes: {},
      },
      visibilityManager: {
        isolatedElements: {},
        setIsolatedElements: () => undefined,
        addToIsolation: () => undefined,
        hiddenElements: {},
        setHiddenElements: () => undefined,
        hide: () => undefined,
        show: () => undefined,
        hiddenSheetShapes: {},
        hideSheetShape: () => undefined,
        showSheetShape: () => undefined,
      },
      autoHiddenLegends: {},
      activeTool: ActiveTool.Default,
      setActiveTool: () => undefined,
      measurement: { isMeasuring: false },
      drawing: {
        isDrawing: false,
        drawingMode: 'Polygon' as any,
        setDrawingMode: () => undefined,
        results: undefined,
        hasResults: false,
        createShape: (async (shapeInput: any, drainLineData?: any) => ({
          data: undefined,
          error: undefined,
          stale: false,
          operation: undefined,
          hasNext: false,
        })) as any,
        createShapeResult: {
          fetching: false,
          error: undefined,
          data: undefined,
          stale: false,
        },
        editShape: () => undefined,
        updateShape: async () => undefined,
        updateShapeResult: {
          fetching: false,
          error: undefined,
          data: undefined,
          stale: false,
        },
        shapeToEdit: null,
        isMagicPolygonLoading: false,
        drainLineData: null,
        setDrainLineData: () => undefined,
        minHeight: 0.26,
        setMinHeight: () => undefined,
      },
      pdfGhosting: {
        opacity: 1,
        setOpacity: () => undefined,
      },
      showSheetShapeLabels: false,
      setShowSheetShapeLabels: () => undefined,
      colorization: {
        colorization: null,
        colorSection: () => undefined,
        clearColors: () => undefined,
        pattern: null,
        setPattern: () => undefined,
        clearPattern: () => undefined,
      },
      selectedDbIds: {},
      setSelectedDbIds: () => undefined,
      selectDbIds: () => undefined,
      toggleSelectDbId: () => undefined,
      propertyData: undefined,
      isLoading: false,
    }),
    [projectId]
  );

  const [{ data: projectData, fetching: projectFetching }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId },
  });

  // Re-add query for project shapes
  const [{ data: shapesData, fetching: shapesFetching }] = useQuery({
    query: GetShapesForProjectDeepDocument,
    variables: { projectId },
  });

  // Query for order entries - only run when orderId is present
  const [{ data: orderData, fetching: orderFetching }] =
    useQuery<GetOrderDeepQuery>({
      query: GetOrderDeepDocument,
      variables: { id: orderId! },
      pause: !orderId, // Pause the query when orderId is undefined
    });

  const shapes = useMemo(() => {
    return shapesData?.project?.shapes ?? [];
  }, [shapesData]);

  const { selectedIds } = useContext(SelectedEntryContext);

  // Convert selected IDs to actual entry objects - only when we have order data
  const selectedEntries = useMemo(() => {
    if (!orderData?.order?.orderEntries || selectedIds.size === 0 || !orderId) {
      return [];
    }

    const allEntries: CachedEntry[] = [];

    // Helper function to recursively collect all entries
    const collectAllEntries = (entries: any[]): void => {
      entries.forEach((e: OrderEntryDeepFragment) => {
        const cachedEntry: CachedEntry = {
          id: e.id,
          name: e.orderEntryCache?.value?.name ?? 'Unnamed Entry',
          resolvedQuantity: e.orderEntryCache?.value?.resolvedQuantity ?? null,
          color: e.orderEntryCache?.value?.color ?? null,
          resolvedElementIdentifiersDbid:
            e.orderEntryCache?.value?.resolvedElementIdentifiersDbid ?? null,
          subRows: e.orderEntryCache?.value?.subRows
            ? (e.orderEntryCache.value.subRows as CachedEntry[])
            : null,
          customFields: e.orderEntryCache?.value?.customFields ?? null,
        };

        allEntries.push(cachedEntry);

        // Recursively process subrows with the same ID generation logic as MobileOrderEntries
        if (cachedEntry.subRows) {
          const processSubRows = (
            subRows: any[],
            parentId: string,
            depth = 0
          ): void => {
            subRows.forEach((subRow, index) => {
              const uniqueId = `${parentId}_d${depth}_i${index}`;
              const processedSubRow = {
                ...subRow,
                id: uniqueId,
              };
              allEntries.push(processedSubRow);

              if (subRow.subRows && Array.isArray(subRow.subRows)) {
                processSubRows(subRow.subRows, uniqueId, depth + 1);
              }
            });
          };

          processSubRows(cachedEntry.subRows, e.id, 1);
        }
      });
    };

    collectAllEntries(orderData.order.orderEntries);

    // Filter to only selected entries
    return allEntries.filter((entry) => selectedIds.has(entry.id));
  }, [orderData?.order?.orderEntries, selectedIds, orderId]);

  const globalOffset =
    projectData?.project?.settings?.viewerGlobalOffset ?? null;

  // Helper function to get color for an element from a specific entry
  // eslint-disable-next-line complexity
  const getColorForElement = (
    entry: CachedEntry,
    modelUrn: string,
    dbId: number
  ): string | null => {
    const hasSubRows = entry.subRows && entry.subRows.length > 0;
    const isGradientColor = entry.color?.includes('gradient');

    if (isGradientColor && hasSubRows) {
      // Gradient entry: Check if this element belongs to any subrow first
      for (const subRow of entry.subRows ?? []) {
        if (
          subRow.resolvedElementIdentifiersDbid?.some(
            (e: any) => e.modelUrn === modelUrn && e.dbIds.includes(dbId)
          )
        ) {
          return extractFirstHexColor(subRow.color ?? '#000000');
        }
      }
      // If element belongs to gradient parent but not found in subrows, use parent color
      if (
        entry.resolvedElementIdentifiersDbid?.some(
          (e: any) => e.modelUrn === modelUrn && e.dbIds.includes(dbId)
        )
      ) {
        return extractFirstHexColor(entry.color ?? '#000000');
      }
    } else {
      // Non-gradient entry: Use entry's own color
      if (
        entry.resolvedElementIdentifiersDbid?.some(
          (e: any) => e.modelUrn === modelUrn && e.dbIds.includes(dbId)
        )
      ) {
        return extractFirstHexColor(entry.color ?? '#000000');
      }
    }
    return null; // Element doesn't belong to this entry
  };

  // ViewerContent applies effects based on the viewer from context.
  function ViewerContent({
    entries,
  }: {
    entries: CachedEntry[];
  }): React.ReactElement {
    const { viewer, loadedModels } = useViewer();
    const { isolate, show } = useVisibility();
    const { colorSection, clearColors } = useColorization();

    useEffect(() => {
      // Always clear previous colors and reset isolation when effect runs or entries change
      clearColors();
      show({}); // Reset isolation

      if (!entries.length) {
        isolate({});
        viewer?.resize();
        return;
      }

      const allElementsToHighlight: DbIdsPerModel = {};

      // Collect all elements from all selected entries
      entries.forEach((entry) => {
        entry.resolvedElementIdentifiersDbid?.forEach(({ modelUrn, dbIds }) => {
          allElementsToHighlight[modelUrn] = [
            ...(allElementsToHighlight[modelUrn] || []),
            ...dbIds,
          ];
        });
      });

      isolate(allElementsToHighlight);

      // For multi-selection, use a simple color strategy
      let colorFunc: (modelUrn: string, dbId: number) => string;

      if (entries.length === 1) {
        // Single entry selected - use helper function
        const entry = entries[0];
        colorFunc = (modelUrn: string, dbId: number): string => {
          return getColorForElement(entry, modelUrn, dbId) ?? '#000000';
        };
      } else {
        // Multiple entries selected - use helper function for each entry
        colorFunc = (modelUrn: string, dbId: number): string => {
          for (const entry of entries) {
            const color = getColorForElement(entry, modelUrn, dbId);
            if (color) {
              return color;
            }
          }
          return '#000000'; // Default black
        };
      }

      colorSection(colorFunc, allElementsToHighlight);

      viewer?.resize();

      return () => {
        clearColors();
        show({});
        viewer?.resize();
      };
    }, [
      entries,
      isolate,
      show,
      colorSection,
      clearColors,
      loadedModels,
      viewer,
    ]);
    // loadedModels is kept if colorFunc needs it, viewer if resize is direct.

    return <></>;
  }

  // Check if we're currently fetching and don't have required data yet
  // Only require orderId-related data when orderId is present
  const isLoading =
    projectFetching ||
    shapesFetching ||
    (orderId && orderFetching) ||
    !globalOffset;

  if (isLoading) {
    return (
      <Box p={4} textAlign="center" height="100vh">
        <Spinner />
      </Box>
    );
  }

  return (
    // This Box defines the overall screen boundaries for the mobile viewer page.
    <Box height="100vh" width="100vw" overflow="hidden" position="relative">
      <AutodeskViewing
        projectId={projectId}
        setViewingInitialized={setIsViewingInitialized}
      >
        <ViewerModeProvider value={viewerModeContextValue}>
          <ForgeContextMenuProvider>
            <SheetContextMenuProvider>
              <SheetViewerContext.Provider value={sheetViewerValue}>
                <SheetsViewerV2Context.Provider value={sheetsViewerV2Value}>
                  {/* This GridItem is the dedicated mount point for the Autodesk Viewer. */}
                  {/* ForgeViewerProvider will find this by its ID. */}
                  {/* REMOVED a GridItem from here */}
                  {/* ForgeViewerProvider itself doesn't render a DOM element here, but provides context */}
                  {/* and initializes the viewer on the element with id="forge-viewer" */}
                  {/* Wrap ForgeViewerProvider in a Box to control its layout and apply styling */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    zIndex={0} // Keep viewer at base z-index
                    sx={{
                      // Style the container div that ForgeViewerProvider renders (which has id="forge-viewer")
                      // This ensures the container itself fills the Box we've created.
                      '#forge-viewer': {
                        position: 'absolute', // Important for it to obey parent's bounds
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: '100%', // Take full height of the parent Box
                        width: '100%', // Take full width of the parent Box
                      },
                      // Ensure the viewer's internal rendering elements take full space within their container.
                      '.adsk-viewing-viewer, .canvas-wrap, canvas': {
                        height: '100% !important',
                        width: '100% !important',
                      },
                    }}
                  >
                    <ProjectHistoryProvider>
                      <ForgeViewerProvider
                        projectId={projectId}
                        isViewingInitialized={isViewingInitialized}
                        globalOffset={globalOffset}
                      >
                        <>
                          <MobileViewerSettings />
                          <ViewerContent entries={selectedEntries} />
                          <ProjectShapesRenderer
                            projectId={projectId}
                            shapes={shapes}
                          />
                        </>
                      </ForgeViewerProvider>
                    </ProjectHistoryProvider>
                  </Box>
                </SheetsViewerV2Context.Provider>
              </SheetViewerContext.Provider>
            </SheetContextMenuProvider>
          </ForgeContextMenuProvider>
        </ViewerModeProvider>
      </AutodeskViewing>
    </Box>
  );
}
